import { useCallback, useEffect, useState } from "react";
import { useBlocker } from "react-router-dom";

/**
 * Guards against silently losing unsaved/in-progress work when the user tries to leave.
 * Covers three exit paths: React Router navigation (links, browser back/forward) via
 * useBlocker, tab close/refresh via beforeunload, and local (non-route) UI actions via
 * guardedNavigate, e.g. a wizard step click or a dialog close that doesn't change the URL.
 */
export function useUnsavedChangesGuard(hasUnsavedChanges: boolean) {
  const blocker = useBlocker(
    ({ currentLocation, nextLocation }) =>
      hasUnsavedChanges && currentLocation.pathname !== nextLocation.pathname
  );

  const [pendingAction, setPendingAction] = useState<(() => void) | null>(null);

  useEffect(() => {
    if (!hasUnsavedChanges) return;
    const handler = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = "";
    };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [hasUnsavedChanges]);

  const guardedNavigate = useCallback(
    (action: () => void) => {
      if (hasUnsavedChanges) {
        setPendingAction(() => action);
      } else {
        action();
      }
    },
    [hasUnsavedChanges]
  );

  const isBlocked = blocker.state === "blocked" || pendingAction !== null;

  const confirmLeave = useCallback(() => {
    if (blocker.state === "blocked") {
      blocker.proceed();
    } else if (pendingAction) {
      const action = pendingAction;
      setPendingAction(null);
      action();
    }
  }, [blocker, pendingAction]);

  const cancelLeave = useCallback(() => {
    if (blocker.state === "blocked") blocker.reset();
    setPendingAction(null);
  }, [blocker]);

  return { isBlocked, confirmLeave, cancelLeave, guardedNavigate };
}
